import { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './contexts/AuthContext';
import { ErrorBoundary, LoadingSpinner, MainLayout, ProtectedRoute } from './components';
import { LoginPage, DashboardPage, PatientsPage, PatientDetailPage, PatientFormPage } from './pages';
import { AppointmentsPage } from './pages/AppointmentsPage';
import { CatalogPage } from './pages/CatalogPage';
import { SettingsPage } from './pages/SettingsPage';
import './lib/i18n';

// Query client configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Loading component
function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <LoadingSpinner text="Hleð..." size="lg" />
    </div>
  );
}

// Placeholder components (will be implemented in later phases)

function TreatmentsPage() {
  return (
    <div className="bg-white rounded-xl shadow-soft p-6">
      <h1 className="text-2xl font-bold">Meðferðir</h1>
      <p className="mt-4 text-gray-600">Treatments page coming in Phase 5.7</p>
    </div>
  );
}

function InvoicesPage() {
  return (
    <div className="bg-white rounded-xl shadow-soft p-6">
      <h1 className="text-2xl font-bold">Reikningar</h1>
      <p className="mt-4 text-gray-600">Invoices page coming in Phase 5.7</p>
    </div>
  );
}


function NotFoundPage() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-brand-primary">404</h1>
        <p className="mt-4 text-xl text-gray-600">Síða fannst ekki</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                <Route path="/login" element={<LoginPage />} />

                {/* Protected routes with layout */}
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <DashboardPage />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/patients"
                  element={
                    <ProtectedRoute requiredPermission="view">
                      <MainLayout>
                        <PatientsPage />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/patients/:id"
                  element={
                    <ProtectedRoute requiredPermission="view">
                      <MainLayout>
                        <PatientDetailPage />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/patients/:id/edit"
                  element={
                    <ProtectedRoute requiredPermission="edit">
                      <MainLayout>
                        <PatientFormPage />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/appointments"
                  element={
                    <ProtectedRoute requiredPermission="schedule">
                      <MainLayout>
                        <AppointmentsPage />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/treatments"
                  element={
                    <ProtectedRoute requiredPermission="view">
                      <MainLayout>
                        <TreatmentsPage />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/invoices"
                  element={
                    <ProtectedRoute requiredPermission="billing">
                      <MainLayout>
                        <InvoicesPage />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/catalog"
                  element={
                    <ProtectedRoute requiredPermission="view">
                      <MainLayout>
                        <CatalogPage />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <MainLayout>
                        <SettingsPage />
                      </MainLayout>
                    </ProtectedRoute>
                  }
                />

                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </Suspense>
          </BrowserRouter>

          {/* React Query DevTools - only in development */}
          {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
